import { Button, Checkbox, Menu, MenuButton, MenuItem, MenuList, Stack, VStack } from '@chakra-ui/react';
import { useUserContext } from 'context/UserContextProvider';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AiOutlineForm } from 'react-icons/ai';

import { DataTable } from '../../components/Table/DataTable';
import useDsbFormsQuery from '../../hooks/useDsbFormsQuery';
import useMakeCountryMappingQuery from '../../hooks/useMakesCountryQuery';
import useMakesQuery from '../../hooks/useMakesQuery';
import { ContentCard } from '../../layout/ContentCard';

const MakeFormMappingCard = () => {
  const { t } = useTranslation();
  const {
    config: { countries = [] },
  } = useUserContext();

  const countiresOptions = Object.values(countries).map((country) => ({
    label: t(`location:countries.${country}`),
    value: country,
  }));

  const [selectedCountriesOptions, setSelectedCountriesOptions] = useState<any[]>([]);
  const [selectedCountriesOptionsExit, setSelectedCountriesOptionsExit] = useState(selectedCountriesOptions);
  const [countriesMenuOpen, setCountriesMenuOpen] = useState(false);
  const [selectedMakeOptions, setSelectedMakeOptions] = useState<any[]>([]);
  const [selectedMakeOptionsExit, setSelectedMakeOptionsExit] = useState(selectedMakeOptions);
  const [makesMenuOpen, setMakesMenuOpen] = useState(false);

  const makesQuery = useMakesQuery();
  const dsbFormsQuery = useDsbFormsQuery();

  const [selectedFormOptions, setSelectedFormOptions] = useState([]);
  const [selectedFormOptionsExit, setSelectedFormOptionsExit] = useState(selectedCountriesOptions);
  const [formMenuOpen] = useState(false);

  const makeCountryMappingQuery = useMakeCountryMappingQuery({
    selectedMakeOptions: selectedMakeOptionsExit,
    selectedCountriesOptions: selectedCountriesOptionsExit,
    selectedFormOptions: selectedFormOptionsExit,
  });

  const makeOptions = makesQuery.data?.reduce(
    (acc, curr) => {
      acc.push({ name: curr.name, id: curr.id });
      return acc;
    },
    [] as { name: string; id: number }[],
  );

  const handleMakeOptionSelect = useCallback(
    (id: number) => {
      if (selectedMakeOptions?.includes(id)) {
        setSelectedMakeOptions(selectedMakeOptions.filter((item) => item !== id));
      } else {
        setSelectedMakeOptions([...selectedMakeOptions, id]);
      }
    },
    [selectedMakeOptions],
  );

  const handleCountriesOptionSelect = useCallback(
    (value: string) => {
      if (selectedCountriesOptions?.includes(value)) {
        setSelectedCountriesOptions(selectedCountriesOptions.filter((item) => item !== value));
      } else {
        setSelectedCountriesOptions([...selectedCountriesOptions, value]);
      }
    },
    [selectedCountriesOptions],
  );

  const columns = useMemo(
    () => [
      {
        Header: t('pages:config_makes.table.columns.make'),
        accessor: (row: any) => makesQuery.data?.find((make) => make?.id === row.make_id)?.name,
        shouldFilter: true,
        Filter: () => (
          <Menu
            autoSelect={false}
            isOpen={makesMenuOpen}
            onOpen={() => {
              setMakesMenuOpen(true);
            }}
            onClose={() => {
              setMakesMenuOpen(false);
              setSelectedMakeOptionsExit(selectedMakeOptions);
            }}
          >
            <MenuButton
              as={Button}
              width={'260px'}
              borderRadius="8px"
              variant="outline"
              bg="#f5f6f7"
              borderColor="#ccd0d5"
              color="#4b4f56"
              _hover={{ bg: '#ebedf0' }}
              _active={{
                bg: '#dddfe2',
                transform: 'scale(0.98)',
                borderColor: '#bec3c9',
              }}
              _focus={{
                boxShadow: '0 0 1px 2px rgba(88, 144, 255, .75), 0 1px 1px rgba(0, 0, 0, .15)',
              }}
            >
              {selectedMakeOptions.length === 0
                ? `${t('components:make')}`
                : selectedMakeOptions.length === 1
                  ? `${selectedMakeOptions.length} ${t('components:make_selected')}`
                  : `${selectedMakeOptions.length} ${t('components:makes_selected')}`}
            </MenuButton>
            <MenuList>
              <VStack align="start">
                {makeOptions?.map(({ name, id }) => (
                  <MenuItem
                    closeOnSelect={false}
                    key={id}
                    onClick={() => {
                      handleMakeOptionSelect(id);
                    }}
                  >
                    <Checkbox
                      isChecked={selectedMakeOptions.includes(id)}
                      onChange={() => {
                        handleMakeOptionSelect(id);
                      }}
                    >
                      {name}
                    </Checkbox>
                  </MenuItem>
                ))}
              </VStack>
            </MenuList>
          </Menu>
        ),
      },
      {
        Header: t('pages:config_makes.table.columns.country'),
        accessor: (row: any) => t(`location:countries.${row.country}`),
        shouldFilter: true,
        Filter: () => (
          <Menu
            autoSelect={false}
            isOpen={countriesMenuOpen}
            onOpen={() => {
              setCountriesMenuOpen(true);
            }}
            onClose={() => {
              setCountriesMenuOpen(false);
              setSelectedCountriesOptionsExit(selectedCountriesOptions);
            }}
          >
            <MenuButton
              as={Button}
              width={'260px'}
              borderRadius="8px"
              variant="outline"
              bg="#f5f6f7"
              borderColor="#ccd0d5"
              color="#4b4f56"
              _hover={{ bg: '#ebedf0' }}
              _active={{
                bg: '#dddfe2',
                transform: 'scale(0.98)',
                borderColor: '#bec3c9',
              }}
              _focus={{
                boxShadow: '0 0 1px 2px rgba(88, 144, 255, .75), 0 1px 1px rgba(0, 0, 0, .15)',
              }}
            >
              {selectedCountriesOptions.length === 0
                ? `${t('components:countries')}`
                : selectedCountriesOptions.length === 1
                  ? `${selectedCountriesOptions.length} ${t('components:country_selected')}`
                  : `${selectedCountriesOptions.length} ${t('components:countries_selected')}`}
            </MenuButton>
            <MenuList>
              <VStack align="start">
                {countiresOptions?.map(({ label, value }) => (
                  <MenuItem
                    closeOnSelect={false}
                    key={value}
                    onClick={() => {
                      handleCountriesOptionSelect(value);
                    }}
                  >
                    <Checkbox
                      isChecked={selectedCountriesOptions.includes(value)}
                      onChange={() => {
                        handleCountriesOptionSelect(value);
                      }}
                    >
                      {label}
                    </Checkbox>
                  </MenuItem>
                ))}
              </VStack>
            </MenuList>
          </Menu>
        ),
      },
      {
        Header: t('pages:config_makes.table.columns.max_age_in_days_backdating'),
        accessor: 'max_age_in_days',
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      makesQuery.data,
      dsbFormsQuery.data,
      selectedMakeOptions,
      selectedMakeOptionsExit,
      selectedCountriesOptions,
      selectedCountriesOptionsExit,
      selectedFormOptions,
      selectedFormOptionsExit,
      formMenuOpen,
      makesMenuOpen,
      countriesMenuOpen,
      makeCountryMappingQuery.data,
    ],
  );

  return (
    <ContentCard
      icon={<AiOutlineForm size={'40px'} />}
      minH="fit-content"
      header={t('pages:config_makes.table.makes_country.header')}
      contentDescription={t('pages:config_makes.table.makes_country.description')}
      data-test-id="make-form-country-card"
    >
      <Stack direction={['column', 'row']} w={'100%'}>
        <Stack direction="column" w={'100%'}>
          <DataTable
            loading={
              makesQuery.isFetching ||
              dsbFormsQuery.isFetching ||
              makeCountryMappingQuery.isFetching ||
              makesQuery.isLoading ||
              dsbFormsQuery.isLoading ||
              makeCountryMappingQuery.isLoading
            }
            data-test-id="makes-dsbform-table"
            data={makeCountryMappingQuery.data || []}
            // @ts-ignore
            hasFilters={true}
            hasSearchInput={false}
            columns={columns}
            isMoreThenOnePage={false}
            resetFilter={() => {
              setSelectedCountriesOptions([]);
              setSelectedMakeOptions([]);
              setSelectedFormOptions([]);
              setSelectedCountriesOptionsExit([]);
              setSelectedMakeOptionsExit([]);
              setSelectedFormOptionsExit([]);
            }}
            filtersInitiallyOpen={selectedCountriesOptions.length > 0 || selectedMakeOptions.length > 0 || selectedFormOptions.length > 0}
            isMultiCheckbox
          />
        </Stack>
      </Stack>
    </ContentCard>
  );
};

export default MakeFormMappingCard;
